import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import BoltIcon from "@mui/icons-material/Bolt";
import HistoryIcon from "@mui/icons-material/History";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import { describeTriggerShort } from "./triggerDescriptions";
import { describeUsedTriggerVariables } from "./triggerVariableLabels";
import RunWithMeetingPicker, { type PickedMeeting } from "./RunWithMeetingPicker";
import RunWithClientPicker, { type PickedClient } from "./RunWithClientPicker";
import type { WorkflowDefinition } from "./workflow.types";

interface WorkflowToolbarProps {
  workflow: WorkflowDefinition;
  isRunning: boolean;
  isStarting: boolean;
  onRename: (name: string) => void;
  onUpdateDescription: (description: string) => void;
  onRequestToggleActive: (nextEnabled: boolean) => void;
  onRun: (triggerContext?: Record<string, unknown>) => void;
  onStop: () => void;
  onDuplicate: () => void;
  onSimulateTrigger: () => void;
  onOpenHistory: () => void;
  theme: "light" | "dark";
  onToggleTheme: () => void;
}

export default function WorkflowToolbar({
  workflow,
  isRunning,
  isStarting,
  onRename,
  onUpdateDescription,
  onRequestToggleActive,
  onRun,
  onStop,
  onDuplicate,
  onSimulateTrigger,
  onOpenHistory,
  theme,
  onToggleTheme,
}: WorkflowToolbarProps) {
  const [name, setName] = useState(workflow.name);
  const [description, setDescription] = useState(workflow.description ?? "");
  const [pickedMeeting, setPickedMeeting] = useState<PickedMeeting | null>(null);
  const [pickedClient, setPickedClient] = useState<PickedClient | null>(null);

  useEffect(() => setName(workflow.name), [workflow._id, workflow.name]);
  useEffect(() => setDescription(workflow.description ?? ""), [workflow._id, workflow.description]);
  // A picked test meeting/client is a one-off override for a manual Run, never
  // part of the workflow's own saved trigger — leaving it set after switching
  // workflows would silently reuse another workflow's test data on the next Run.
  useEffect(() => {
    setPickedMeeting(null);
    setPickedClient(null);
  }, [workflow._id]);

  // Picking a meeting auto-fills the client from it — one change here, and
  // every {{trigger.clientId}} / {{trigger.meetingId}} step in the workflow
  // below picks it up automatically. The client field stays editable
  // afterwards (e.g. to deliberately test a mismatch); picking a different
  // meeting always re-syncs it.
  const handleMeetingChange = (meeting: PickedMeeting | null) => {
    setPickedMeeting(meeting);
    if (meeting?.clientId) {
      setPickedClient({ clientId: meeting.clientId, label: meeting.label.split(" — ")[0] });
    } else {
      setPickedClient(null);
    }
  };
  const clientAutoFilledByMeeting = !!pickedMeeting?.clientId && pickedClient?.clientId === pickedMeeting.clientId;

  // Manually changing the client away from whatever the picked meeting
  // belongs to leaves the meeting stale — both would otherwise be sent to
  // the run with mismatched entities, with no warning. Clearing it here
  // means "meeting" always agrees with "client" whenever both are set.
  const handleClientChange = (client: PickedClient | null) => {
    setPickedClient(client);
    if (pickedMeeting && client && pickedMeeting.clientId !== client.clientId) {
      setPickedMeeting(null);
    }
  };

  const commitName = () => {
    const trimmed = name.trim();
    if (trimmed && trimmed !== workflow.name) onRename(trimmed);
    else setName(workflow.name);
  };

  const commitDescription = () => {
    const trimmed = description.trim();
    if (trimmed !== (workflow.description ?? "").trim()) onUpdateDescription(trimmed);
  };

  const isManualTrigger = workflow.trigger?.type === "manual";

  return (
    <Box
      sx={{
        borderBottom: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2,
        pt: 1.25,
        flexWrap: "wrap",
      }}
    >
      <Tooltip title={name.length > 22 ? name : ""} disableInteractive>
        <TextField
          variant="standard"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={commitName}
          onKeyDown={(e) => {
            if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          }}
          InputProps={{ disableUnderline: true }}
          sx={{
            flex: "1 1 120px",
            minWidth: 0,
            "& input": {
              fontWeight: 700,
              fontSize: 16,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            },
          }}
        />
      </Tooltip>

      <Chip
        size="small"
        label={workflow.enabled ? "Active" : "Inactive"}
        color={workflow.enabled ? "success" : "default"}
        variant={workflow.enabled ? "filled" : "outlined"}
        sx={{ flexShrink: 0 }}
      />

      <FormControlLabel
        sx={{ ml: 0.5, flexShrink: 0 }}
        control={
          <Switch
            size="small"
            checked={workflow.enabled}
            onChange={(e) => onRequestToggleActive(e.target.checked)}
          />
        }
        label={<Typography variant="body2">{workflow.enabled ? "On" : "Off"}</Typography>}
      />

      {isManualTrigger ? (
        <Typography variant="caption" color="text.secondary">
          This workflow only runs when you click Run.
        </Typography>
      ) : (
        <Tooltip
          title={
            workflow.enabled
              ? "Manually simulate this trigger for testing — no real event source is wired to this button yet."
              : "Activate this workflow to enable automatic runs"
          }
        >
          <span>
            <Button
              size="small"
              variant="outlined"
              startIcon={<BoltIcon fontSize="small" />}
              disabled={!workflow.enabled || isRunning || isStarting}
              onClick={onSimulateTrigger}
            >
              Simulate: {describeTriggerShort(workflow.trigger?.type)}
            </Button>
          </span>
        </Tooltip>
      )}

      <Button size="small" variant="outlined" startIcon={<ContentCopyIcon fontSize="small" />} onClick={onDuplicate}>
        Clone
      </Button>

      {isRunning && (
        <Button size="small" variant="contained" color="error" startIcon={<StopIcon />} onClick={onStop}>
          Stop
        </Button>
      )}

      <Button
        size="small"
        variant="contained"
        startIcon={<PlayArrowIcon />}
        disabled={isRunning || isStarting}
        onClick={() => {
          // pickedClient is already kept in sync with pickedMeeting (see
          // handleMeetingChange above) — both can be supplied alone too
          // (client-only for a workflow that never touches a meeting,
          // meeting-only for the common case).
          const context: Record<string, string> = {};
          if (pickedClient) context.clientId = pickedClient.clientId;
          if (pickedMeeting) context.meetingId = pickedMeeting.meetingId;
          onRun(Object.keys(context).length ? context : undefined);
        }}
      >
        {isStarting ? "Starting…" : "Run"}
      </Button>

      <Tooltip title="View run history">
        <IconButton size="small" onClick={onOpenHistory} sx={{ opacity: 0.55, "&:hover": { opacity: 1 } }}>
          <HistoryIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title={theme === "dark" ? "Switch to light canvas" : "Switch to dark canvas"}>
        <IconButton size="small" onClick={onToggleTheme} sx={{ opacity: 0.55, "&:hover": { opacity: 1 } }}>
          {theme === "dark" ? <LightModeOutlinedIcon fontSize="small" /> : <DarkModeOutlinedIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
    </Box>

      {!isRunning && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1.5,
            px: 2,
            py: 1,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "action.hover",
          }}
        >
          <Box sx={{ minWidth: 140 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, display: "block" }}>
              Test variables
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Used by every step below that reads {describeUsedTriggerVariables(workflow.nodes)}
            </Typography>
          </Box>
          <RunWithMeetingPicker
            value={pickedMeeting}
            onChange={handleMeetingChange}
            filterClientId={pickedClient?.clientId}
          />
          <RunWithClientPicker
            value={pickedClient}
            onChange={handleClientChange}
            helperText={clientAutoFilledByMeeting ? "Auto-filled from the selected meeting" : undefined}
          />
        </Box>
      )}

      <TextField
        variant="standard"
        fullWidth
        multiline
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        onBlur={commitDescription}
        placeholder="Add a description — what this workflow does and why, for anyone else who opens it"
        InputProps={{ disableUnderline: true }}
        sx={{ px: 2, pb: 1, pt: 0.25, "& textarea": { fontSize: 12.5, color: "text.secondary" } }}
      />
    </Box>
  );
}
